import Reporter, { defaultReporter } from './reporter';
import { isDebugMode, LogLevel, PostLogLevel } from './config';
import { IS_SNAPSHOT } from './utils';

let logSeq = 0;

export function getNextSeq() {
  return ++logSeq;
}

type TLogData = any[];
export type TlogFunc = (...logArgs: TLogData) => Logger;

//统计支持的相关方法
interface ILinkStatisticAble {
  count: (logName?: string) => Logger;
  tag: (logName?: string) => Logger;
  statistic:
    | ((logName: string, value: number, digest?: string) => Logger)
    | ((value: number) => Logger);
  timeCost: (logName?: string | number, valueOrDigest?: number | string, digest?: string) => Logger;
  timeCostSince:
    | ((logName: string, sinceTag: string, digest?: string) => Logger)
    | ((sinceTag: string) => Logger);
  getTimeCostSince: (logName: string) => number;
}

const TIME_TAG_CACHE = {};
const TIME_START =
  window['performance'] &&
  window['performance']['timing'] &&
  window['performance']['timing']['navigationStart'];

export default class Logger implements ILinkStatisticAble {
  constructor(nameSpacePrefix: string, reporter: Reporter) {
    this.nameSpacePrefix = nameSpacePrefix;
    this.reporter = reporter;
  }

  //module前缀
  private nameSpacePrefix = '';
  private reporter: Reporter;

  private logInternal(type: string, logArgs: any[]) {
    // 不上报的日志不需要累加seq
    const needPost = !(IS_SNAPSHOT || isDebugMode() || LogLevel[type] < PostLogLevel);

    const seq = needPost ? ++logSeq : logSeq;
    this.reporter.doLog(this.nameSpacePrefix, type, logArgs, seq);
  }

  //支持链式调用
  private makeLinkable() {
    return this;
  }

  private getLogFunc = (funcName: string) => {
    return (...logArgs: any[]) => {
      try {
        //健壮性
        this.logInternal(funcName, logArgs);
      } catch (e) {}
      return this.makeLinkable();
    };
  };

  public debug: TlogFunc = this.getLogFunc('debug');

  public log: TlogFunc = this.getLogFunc('log');

  public info: TlogFunc = this.getLogFunc('info');

  public warn: TlogFunc = this.getLogFunc('warn');

  public error: TlogFunc = (...logArgs: any[]) => {
    return this.getLogFunc('error')(...logArgs);
  };

  private countWithoutLog(logName: string, digest?: string) {
    this.reporter.count(logName, digest);
    return this.makeLinkable();
  }

  public count = (logName: string) => {
    this.info('docLogger.count', logName);
    return this.countWithoutLog(logName);
  };

  // 支持摘要的统计
  public countWithDigest(logName: string, digest: string) {
    this.info('docLogger.count', logName, digest);
    return this.countWithoutLog(logName, digest);
  }

  public timeCost = (logName: string, valueOrDigest?: number | string, digest?: string) => {
    let value;
    if (typeof valueOrDigest !== 'number') {
      value = Date.now() - TIME_START;
    } else {
      value = valueOrDigest;
    }

    if (!digest && typeof valueOrDigest === 'string') {
      digest = valueOrDigest;
    }

    return this.statistic(logName, value, digest);
  };

  public tag = (logName: string) => {
    TIME_TAG_CACHE[logName] = Date.now();
    return this.makeLinkable();
  };

  public timeCostSince = (logName: string, sinceTag: string, digest?: string) => {
    const startTime = TIME_TAG_CACHE[sinceTag];
    if (startTime) {
      const value = Date.now() - startTime;
      return this.statistic(logName, value, digest);
    } else {
      console.error(`没有tag${sinceTag}开始时间`);
      return this.makeLinkable();
    }
  };

  public getTimeCostSince = (sinceTag: string) => {
    const startTime = TIME_TAG_CACHE[sinceTag];
    if (startTime) {
      return Date.now() - startTime;
    } else {
      return 0;
    }
  };

  public getTimeCost = () => {
    if (TIME_START) {
      return Date.now() - TIME_START;
    } else {
      return 0;
    }
  };

  public statistic = (logName: string, value: number, digest?: string) => {
    value = Math.round(value || 0);
    this.info('docLogger.statistic', logName, value, digest);
    this.reporter.gague(logName, value, digest);
    return this.makeLinkable();
  };
}

export const defaultLogger = new Logger('', defaultReporter);

const loggerCache: {
  [nameSpacePrefix: string]: Logger;
} = {};

export function getLogger(nameSpacePrefix: string) {
  if (!loggerCache[nameSpacePrefix]) {
    loggerCache[nameSpacePrefix] = new Logger(nameSpacePrefix, defaultReporter);
  }
  return loggerCache[nameSpacePrefix];
}
